/**
 * Alpine host for the location prompts that the server lists in `pending_prompts` (docs#10).
 *
 * The server renders the first prompt kind the web knows into
 * layouts/partials/location-prompt-host.twig. This component shows it at most once per browser
 * tab session. Each action posts the prompt_id of the rendered prompt, so the server refuses an
 * action on a prompt that a newer one replaced.
 */

import apiClient from '../api-client.js';

export const SESSION_KEY = 'locationPrompt:shown';

/** True when this tab has not shown a location prompt yet. Claims the slot. */
export function claimSessionSlot(storage) {
    try {
        if (storage.getItem(SESSION_KEY) !== null) {
            return false;
        }
        storage.setItem(SESSION_KEY, '1');
        return true;
    } catch {
        // Without sessionStorage the host cannot keep the once-per-session rule, so it shows nothing.
        return false;
    }
}

/** A null autoCityDefault means the prompt offers no auto_city choice, so Accept sends none. */
export function locationPromptHost(kind, promptId = '', autoCityDefault = null) {
    return {
        kind,
        promptId,
        offersAutoCity: autoCityDefault !== null,
        autoCity: autoCityDefault === true,
        visible: false,
        busy: false,
        expired: false,
        error: '',

        init() {
            this.visible = claimSessionSlot(window.sessionStorage);
        },

        body(extra = {}) {
            const body = new URLSearchParams();
            if (this.promptId) {
                body.append('prompt_id', this.promptId);
            }
            for (const [key, value] of Object.entries(extra)) {
                body.append(key, value);
            }
            return body;
        },

        async dismiss() {
            if (this.busy) {
                return;
            }
            this.busy = true;
            this.visible = false;
            try {
                await apiClient.request(`/location-prompts/${encodeURIComponent(this.kind)}/dismiss`, {
                    method: 'POST',
                    body: this.body(),
                });
            } catch {
                // The prompt stays pending on the server and can show again in a later session.
            } finally {
                this.busy = false;
            }
        },

        accept() {
            return this.act('city_nudge/accept', this.offersAutoCity ? { auto_city: this.autoCity ? 'on' : 'off' } : {});
        },

        undo() {
            return this.act('city_auto_notice/undo', {});
        },

        /** Reloads on success, so the page shows the new city. */
        async act(path, extra) {
            if (this.busy) {
                return;
            }
            this.busy = true;
            this.error = '';
            const dataset = this.$el?.dataset ?? {};
            try {
                const response = await apiClient.request(`/location-prompts/${path}`, {
                    method: 'POST',
                    body: this.body(extra),
                });
                if (response.ok) {
                    window.location.reload();
                    return;
                }
                // A 409 means this prompt can no longer act in this session, so its actions go away.
                this.expired = response.status === 409;
                this.error = this.expired ? (dataset.expiredMessage ?? '') : (dataset.errorMessage ?? '');
            } catch {
                this.error = dataset.errorMessage ?? '';
            }
            this.busy = false;
        },
    };
}
